/**
 * LeaderboardModal - Sortable leaderboard display with participant stats
 */
import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '../../context/ThemeContext';

// Sort option buttons
const SORT_OPTIONS = [
  { 
    key: 'score', 
    label: 'Punkte',
    icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  },
  { 
    key: 'correct', 
    label: 'Richtige',
    icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  },
  { 
    key: 'streak', 
    label: 'Streak',
    icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  },
  { 
    key: 'time', 
    label: 'Zeit',
    icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  },
  { 
    key: 'name', 
    label: 'Name',
    icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
    </svg>
  }
];

// Helper: Get medal styling for rank
const getMedalStyle = (rank) => {
  switch (rank) {
    case 1: return 'bg-yellow-400 text-yellow-900';
    case 2: return 'bg-gray-300 text-gray-900';
    case 3: return 'bg-amber-600 text-amber-100';
    default: return 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400';
  }
};

// Helper: Format response time
const formatTime = (ms) => {
  if (!ms || ms === 0) return '-';
  const seconds = ms / 1000;
  return seconds < 1 ? `${Math.round(ms)}ms` : `${seconds.toFixed(1)}s`;
};

// Sort Button Component
const SortButton = ({ sortKey, label, icon, active, onClick, isDark }) => (
  <button
    onClick={() => onClick(sortKey)}
    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium theme-transition ${
      active
        ? 'bg-primary-600 text-white'
        : isDark 
          ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
    }`}
  >
    {icon}
    {label}
  </button>
);

SortButton.propTypes = {
  sortKey: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  icon: PropTypes.node,
  active: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  isDark: PropTypes.bool.isRequired
};

// Leaderboard Row Component
const LeaderboardRow = ({ participant, isDark }) => {
  const { rank, name, totalScore, correctCount, answeredCount, correctPercentage, maxStreak, avgResponseTime } = participant;
  const medalStyle = getMedalStyle(rank);
  
  return (
    <tr 
      className={`${
        isDark 
          ? 'border-b border-gray-700/50 hover:bg-gray-700/30'
          : 'border-b border-gray-300 hover:bg-gray-100'
      } ${
        rank <= 3 ? (isDark ? 'bg-warning-500/10' : 'bg-yellow-50') : ''
      }`}
    >
      <td className="py-3 pl-2">
        <span className={`flex items-center justify-center w-8 h-8 rounded-full font-bold text-sm ${medalStyle}`}>
          {rank}
        </span>
      </td>
      <td className="py-3">
        <span className={`font-medium ${rank <= 3 ? 'text-yellow-600 dark:text-yellow-300' : ''}`}>
          {name}
        </span>
      </td>
      <td className="py-3 text-center">
        <span className="font-bold text-primary-400 text-lg">{totalScore}</span>
      </td>
      <td className="py-3 text-center">
        <span className="text-success-400">
          {correctCount}/{answeredCount}
          <span className={`text-sm ml-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>({correctPercentage}%)</span>
        </span>
      </td>
      <td className="py-3 text-center">
        <span className={`font-medium ${maxStreak >= 3 ? 'text-orange-400' : (isDark ? 'text-gray-400' : 'text-gray-500')}`}>
          {maxStreak > 0 ? `${maxStreak}x` : '-'}
        </span>
      </td>
      <td className="py-3 text-center">
        <span className={`${avgResponseTime > 0 && avgResponseTime < 5000 ? 'text-success-400' : (isDark ? 'text-gray-400' : 'text-gray-500')}`}>
          {formatTime(avgResponseTime)}
        </span>
      </td>
    </tr>
  );
};

LeaderboardRow.propTypes = {
  participant: PropTypes.shape({
    id: PropTypes.string.isRequired,
    rank: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    totalScore: PropTypes.number.isRequired,
    correctCount: PropTypes.number.isRequired,
    answeredCount: PropTypes.number.isRequired,
    correctPercentage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    maxStreak: PropTypes.number.isRequired,
    avgResponseTime: PropTypes.number.isRequired
  }).isRequired,
  isDark: PropTypes.bool.isRequired
};

/**
 * Main LeaderboardModal Component
 */
function LeaderboardModal({ 
  isOpen, 
  onClose, 
  participants, 
  answers, 
  questions 
}) {
  const [sortBy, setSortBy] = useState('score');
  const { isDark } = useTheme();

  // Calculate and sort leaderboard data
  const leaderboardData = useMemo(() => {
    if (!isOpen) return [];

    const data = Object.entries(participants).map(([id, data]) => {
      let correctCount = 0;
      let totalResponseTime = 0;
      let answeredCount = 0;
      let tempStreak = 0;
      let maxStreak = 0;

      questions.forEach((question, questionIndex) => {
        const questionAnswers = answers[questionIndex] || {};
        const participantAnswer = questionAnswers[id];
        
        if (participantAnswer !== undefined) {
          answeredCount++;
          const answerData = typeof participantAnswer === 'object' 
            ? participantAnswer 
            : { selectedIndex: participantAnswer };
          const answerIndex = answerData.selectedIndex;
          
          const correctIndices = question.correctAnswerIndices || [question.correctAnswerIndex];
          const isCorrect = correctIndices.includes(answerIndex);
          
          if (isCorrect) {
            correctCount++;
            tempStreak++;
            maxStreak = Math.max(maxStreak, tempStreak);
          } else {
            tempStreak = 0;
          }
          
          if (answerData.responseTimeMs) {
            totalResponseTime += answerData.responseTimeMs;
          }
        }
      });

      const avgResponseTime = answeredCount > 0 ? totalResponseTime / answeredCount : 0;

      return {
        id,
        name: data.name || 'Unbekannt',
        totalScore: data.totalScore || correctCount * 100,
        correctCount,
        answeredCount,
        avgResponseTime,
        currentStreak: data.currentStreak || 0,
        maxStreak,
        correctPercentage: answeredCount > 0 ? ((correctCount / answeredCount) * 100).toFixed(0) : 0
      };
    });

    // Sort based on selected criteria
    data.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'correct':
          return b.correctCount - a.correctCount || b.totalScore - a.totalScore;
        case 'streak':
          return b.maxStreak - a.maxStreak || b.totalScore - a.totalScore;
        case 'time':
          return a.avgResponseTime - b.avgResponseTime || b.totalScore - a.totalScore;
        case 'score':
        default:
          return b.totalScore - a.totalScore || b.correctCount - a.correctCount;
      }
    });

    // Add rank
    return data.map((p, index) => ({ ...p, rank: index + 1 }));
  }, [isOpen, participants, answers, questions, sortBy]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div 
        className={`rounded-xl w-full max-w-4xl max-h-[85vh] shadow-2xl flex flex-col ${
          isDark ? 'bg-gray-800' : 'bg-white'
        }`}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`flex justify-between items-center p-4 ${
          isDark ? 'border-b border-gray-700' : 'border-b border-gray-300'
        }`}>
          <h3 className={`text-xl font-bold flex items-center gap-2 ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
            </svg>
            Rangliste
            <span className={`text-sm font-normal ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              ({leaderboardData.length} Teilnehmer)
            </span>
          </h3>
          <button 
            onClick={onClose} 
            className={`p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 theme-transition ${
              isDark ? 'text-gray-400' : 'text-gray-600'
            }`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Sort Options */}
        <div className={`flex gap-2 p-4 flex-wrap ${
          isDark ? 'border-b border-gray-700 bg-gray-800/50' : 'border-b border-gray-300 bg-gray-50'
        }`}>
          <span className={`text-sm self-center mr-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Sortieren nach:
          </span>
          {SORT_OPTIONS.map(({ key, label, icon }) => (
            <SortButton
              key={key}
              sortKey={key}
              label={label}
              icon={icon}
              active={sortBy === key}
              onClick={setSortBy}
              isDark={isDark}
            />
          ))}
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto p-4">
          {leaderboardData.length === 0 ? (
            <div className={`text-center py-12 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              <svg className="w-16 h-16 mx-auto mb-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              <p>Noch keine Teilnehmer</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className={`text-left text-sm ${
                isDark 
                  ? 'text-gray-400 border-b border-gray-700' 
                  : 'text-gray-600 border-b border-gray-300'
              }`}>
                <tr>
                  <th className="pb-3 pl-2 w-12">#</th>
                  <th className="pb-3">Name</th>
                  <th className="pb-3 text-center">Punkte</th>
                  <th className="pb-3 text-center">Richtig</th>
                  <th className="pb-3 text-center">Max Streak</th>
                  <th className="pb-3 text-center">Ø Zeit</th>
                </tr>
              </thead>
              <tbody>
                {leaderboardData.map((p) => (
                  <LeaderboardRow key={p.id} participant={p} isDark={isDark} />
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className={`p-4 flex justify-end ${
          isDark ? 'border-t border-gray-700 bg-gray-800/50' : 'border-t border-gray-300 bg-gray-50'
        }`}>
          <button
            onClick={onClose}
            className={`px-5 py-2 rounded-lg theme-transition ${
              isDark 
                ? 'bg-gray-700 hover:bg-gray-600 text-white' 
                : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
            }`}
          >
            Schließen
          </button>
        </div>
      </div>
    </div>
  );
}

LeaderboardModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  participants: PropTypes.object.isRequired,
  answers: PropTypes.object.isRequired,
  questions: PropTypes.array.isRequired
};

export default LeaderboardModal;
